import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { take, tap } from 'rxjs';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import {
  ActiveSessionsByAnywhereClientModel,
  ActiveSessionModel,
  ActiveSessionsByInitialsModel,
} from 'src/app/models/active-session.model';
import { LicenseEnforcementModel } from 'src/app/models/license-enforcement.model';
import { DialogService } from 'src/app/services/dialog.service';
import { SessionService } from 'src/app/services/session.service';
import { ToastService } from 'src/app/services/toast.service';
import { WindowService } from 'src/app/services/window.service';
import { environment } from 'src/environments/environment';
import { UAParser } from 'ua-parser-js';

@Component({
  selector: 'tx-session-takeover-dialog',
  templateUrl: './session-takeover-dialog.component.html',
  styleUrls: ['./session-takeover-dialog.component.scss'],
})
export class SessionTakeoverDialogComponent implements OnInit, OnDestroy {
  @Input() sessions: ActiveSessionsByAnywhereClientModel[] = [];
  public selectedSession: ActiveSessionModel | undefined;
  public closeEnabled: boolean = false;
  public submitUrl: string;
  public sessionId = SESSION_ID;
  public loading = false;
  public loadingPreview = true;
  public savedPreviews: any = {};
  public previewImage: Blob | undefined;
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;

  constructor(
    private sessionService: SessionService,
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private dialogService: DialogService,
    private windowService: WindowService
  ) {
    this.submitUrl = environment.endpoints.apiUrl + '/session/TakeOverSessionInNewTab';
  }

  @HostListener('window:focus', ['$event'])
  onWindowFocus(event: Event): void {
    this.getSessions();
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {

  }

  onSessionSelected(session: any) {
    this.selectedSession = session;
  }

  getUserAgentName(userAgent: string): string {
    let parser = new UAParser(userAgent);
    const result = parser.getResult();

    return `${result.browser.name} on ${result.os.name} ${result.os.version}`;
  }

  onTakeoverClicked(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    this.selectedSession = session;

    const filenameInput = document.getElementById('filename') as HTMLInputElement;
    if (filenameInput) {
      // filenameInput.value = this.selectedSession?.filename ?? '';
      filenameInput.value = this.selectedSession?.activeDcmPath ?? '';
    }

    const activeSessionId = document.getElementById('activeSessionId') as HTMLInputElement;
    if (activeSessionId) {
      activeSessionId.value = this.selectedSession?.sessionId ?? '';
    }

    const activeSessionAnswerPoolId = document.getElementById('activeSessionAnswerPoolId') as HTMLInputElement;
    if (activeSessionAnswerPoolId) {
      activeSessionAnswerPoolId.value = this.selectedSession?.answerPoolId ?? '';
    }

    // current sessionId for authentication
    const sessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
    if (sessionId) {
      sessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
    }

    this.openInNewTabForm.nativeElement.submit();
    this.handleResultAndRemoveSession(session, initialsGroup, userAgentGroup);
  }

  onSaveClicked(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    this.sessionService
      .saveDocumentAndcloseSession(session)
      .pipe(take(1))
      .subscribe((res) => {
        this.handleResultAndRemoveSession(session, initialsGroup, userAgentGroup);
      });
  }

  onCloseClicked(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    return this.dialogService.confirm({
      title: 'Discard Session',
      message: 'Are you sure you want to discard close that session? Any unsaved changes will be lost',
      confirmButtonText: 'Discard',
      rejectButtonText: 'Cancel',
      size: 'sm',
      confirmCallback: () => {
        this.closeSession(session, initialsGroup, userAgentGroup);
      },
      rejectCallback: () => {},
    });
  }

  closeSession(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    this.sessionService
      .closeSession(session)
      .pipe(take(1))
      .subscribe((res) => {
        this.handleResultAndRemoveSession(session, initialsGroup, userAgentGroup);
      });
  }

  public getIconExtension(filename: string): string {
    if (!filename) {
      return 'file-earmark';
    }
    const ext = splitFilename(filename.toLowerCase())?.extension;
    if (ext) {
      return 'filetype-' + ext;
    }
    return 'file-earmark';
  }

  public getSessionThumbnail(session: ActiveSessionModel): any {
    if (this.savedPreviews[session.txTextControlSessionId] != undefined) {
      this.previewImage = this.savedPreviews[session.sessionId];
      this.loadingPreview = false;
    }
    return this.sessionService.getSessionThumbnail(session).subscribe((blob) => {
      this.savedPreviews[session.txTextControlSessionId] = URL.createObjectURL(blob);
      this.previewImage = this.savedPreviews[session.txTextControlSessionId];
      this.loadingPreview = false;
    });
  }

  public clearSessionThumbnail() {
    this.previewImage = undefined;
    this.loadingPreview = true;
  }

  getDocumentName(session: ActiveSessionModel): string {
    let name = session.filename;

    if (!name) {
      name = 'Untitled Document';
    }

    return name;
  }

  getDocumentNameTooltip(session: ActiveSessionModel) {
    let fileName = 'Untitled Document';
    if (session.filename != null) {
      fileName = session.filename;
    }
    let tooltip = fileName;

    if (this.isDirty(session)) {
      tooltip += '. This document has unsaved changes.';
    }
    return tooltip;
  }

  public isDirty(session: ActiveSessionModel): boolean {
    return session.dateAutosaved != null;
  }

  getSessions() {
    this.loading = true;
    this.sessionService
      .getActiveSessions()
      .pipe(
        take(1),
        tap((res: any) => {
          if (res && res.sessions) {
            this.sessions = res.sessions;
  
            if (this.sessions.length === 0) {
              this.activeModal.dismiss();
            }
  
            if (this.sessions.length < res.licenseCount) {
              this.closeEnabled = true;
            }
          }
        }),
        tap((res: any) => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  public showSaveButton(session: ActiveSessionModel): boolean {
    if (this.isPdf(session.activeDcmPath)) {
      return false;
    }

    return session.dateAutosaved != null;
  }

  private isPdf(filename: string): boolean {
    return splitFilename(filename)?.extension === 'pdf';
  }

  private removeSessionFromList(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    initialsGroup.sessions = initialsGroup.sessions.filter((s) => s.sessionId !== session.sessionId);

    if (initialsGroup.sessions.length === 0) {
      userAgentGroup.sessionsByInitials = userAgentGroup.sessionsByInitials.filter(
        (s) => s.initials !== initialsGroup.initials
      );

      if (userAgentGroup.sessionsByInitials.length === 0) {
        this.sessions = this.sessions.filter((s) => s.anywhereClientId !== userAgentGroup.anywhereClientId);

        this.closeEnabled = true;

        if (this.sessions.length === 0) {
          this.activeModal.dismiss();
        }
      }
    }
  }

  private handleResultAndRemoveSession(
    session: ActiveSessionModel,
    initialsGroup: ActiveSessionsByInitialsModel,
    userAgentGroup: ActiveSessionsByAnywhereClientModel
  ) {
    this.removeSessionFromList(session, initialsGroup, userAgentGroup);
  }
}
